/**
 * Public about page with site/author information.
 * 
 * Features:
 * - Author bio
 * - Site description
 * - Contact information
 * 
 * Called by:
 * - App.tsx as /about route
 */
import { Typography, Card, Space, Divider } from 'antd';
import { BookOutlined, EditOutlined, CommentOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

/**
 * Public about page component.
 */
function PublicAboutPage() {
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Page header */}
      <Title
        style={{
          fontSize: '2.5rem',
          fontFamily: "var(--font-serif)",
          fontWeight: 400,
          color: '#f5f5f5',
          marginBottom: '32px',
          textAlign: 'center',
        }}
      >
        About This Compendium
      </Title>

      {/* Introduction */}
      <Card
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          marginBottom: '32px',
        }}
        bodyStyle={{ padding: '32px' }}
      >
        <Paragraph
          style={{
            color: '#d0d0d0',
            fontSize: '1.15rem',
            lineHeight: 1.8,
          }}
        >
          Welcome to the Compendium of All My Knowledge — a personal collection of 
          essays, explorations, and accumulated wisdom on topics that fascinate me. 
          This site serves as both a public journal and a carefully curated archive 
          of ideas worth preserving.
        </Paragraph>
        
        <Paragraph
          style={{
            color: '#d0d0d0',
            fontSize: '1.15rem',
            lineHeight: 1.8,
          }}
        >
          Each essay here represents a journey of understanding. Some are polished 
          pieces refined over time; others are works in progress, evolving as my 
          knowledge deepens. The goal is not perfection but honest exploration — 
          to think clearly and share those thoughts with anyone who might benefit.
        </Paragraph>
      </Card>

      {/* Features section */}
      <Title
        level={3}
        style={{
          color: '#f5f5f5',
          fontFamily: "var(--font-serif)",
          marginBottom: '24px',
        }}
      >
        What You'll Find Here
      </Title>

      <Space direction="vertical" size="large" style={{ width: '100%', marginBottom: '32px' }}>
        <Card
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '8px',
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <Space>
            <BookOutlined style={{ fontSize: '1.5rem', color: '#d4af37' }} />
            <div>
              <Text strong style={{ color: '#f5f5f5', fontSize: '1.1rem' }}>
                In-Depth Essays
              </Text>
              <br />
              <Text style={{ color: '#a0a0a0' }}>
                Long-form pieces exploring ideas thoroughly, with care taken to explain 
                concepts clearly and build understanding step by step.
              </Text>
            </div>
          </Space>
        </Card>

        <Card
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '8px',
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <Space>
            <EditOutlined style={{ fontSize: '1.5rem', color: '#d4af37' }} />
            <div>
              <Text strong style={{ color: '#f5f5f5', fontSize: '1.1rem' }}>
                Living Documents
              </Text>
              <br />
              <Text style={{ color: '#a0a0a0' }}>
                Essays that grow and improve over time. Version history tracks how 
                ideas evolve as understanding deepens.
              </Text>
            </div>
          </Space>
        </Card>

        <Card
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '8px',
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <Space>
            <CommentOutlined style={{ fontSize: '1.5rem', color: '#d4af37' }} />
            <div>
              <Text strong style={{ color: '#f5f5f5', fontSize: '1.1rem' }}>
                Community Discussion
              </Text>
              <br />
              <Text style={{ color: '#a0a0a0' }}>
                Sign in to leave comments and join conversations. Your insights and 
                questions help improve these essays for everyone.
              </Text>
            </div>
          </Space>
        </Card>
      </Space>

      {/* Philosophy section */}
      <Divider style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      <Title
        level={3}
        style={{
          color: '#f5f5f5',
          fontFamily: "var(--font-serif)",
          marginBottom: '24px',
        }}
      >
        Philosophy
      </Title>

      <Card
        style={{
          background: 'rgba(212, 175, 55, 0.05)',
          border: '1px solid rgba(212, 175, 55, 0.2)',
          borderRadius: '12px',
        }}
        bodyStyle={{ padding: '32px' }}
      >
        <blockquote style={{ margin: 0, padding: 0, borderLeft: 'none' }}>
          <Paragraph
            style={{
              color: '#d0d0d0',
              fontSize: '1.2rem',
              fontStyle: 'italic',
              lineHeight: 1.8,
              marginBottom: '16px',
            }}
          >
            "The best way to learn is to teach, and the best way to teach is to write. 
            Writing forces clarity of thought — you cannot hide behind vague understanding 
            when words must hit the page."
          </Paragraph>
        </blockquote>
        <Text type="secondary">
          — The guiding principle behind this compendium
        </Text>
      </Card>

      <Paragraph
        style={{
          color: '#a0a0a0',
          marginTop: '32px',
          textAlign: 'center',
        }}
      >
        Thank you for reading. I hope you find something valuable here.
      </Paragraph>
    </div>
  );
}

export default PublicAboutPage;
